<template>
  <img
    ref="image"
    :src="lazy ? '' : srcWithoutLazy"
    :srcset="lazy ? '' : srcsetWithoutLazy"
    :data-src="isFocal && ($mqAliases.xs || $mqAliases.sm) ? updateSrcFocal : updateSrc"
    :data-srcset="isFocal && ($mqAliases.xs || $mqAliases.sm) ? updateSrcsetFocal : updateSrcset"
    :data-pswidth="psimg.width"
    :data-psheight="psimg.height"
    :data-psmssrc="psimg.mssrc"
    :data-pssrc="psimg.src"
    :alt="alt !== null ? alt : srcWithoutLazy.substring(srcWithoutLazy.lastIndexOf('/')+1)"

    :sizes="sizes"
    :style="style"
    class="AppImage"
    :class="loaded ? '-loaded' : ''"
    @load="$emit('load')"
  />
</template>
<script>
import lozad from 'lozad';
import imageHelper from '@/Scripts/lib/helper/imageHelper.js';
/**
 * Image component for viewing Cloudinary images.
 * (can view images from other sources also: just skip Path and Version parameters in imageObject)
 * @displayName Image Cloudinary
 * @version 2.0.0
 */
export default {
  name: 'ImageContentComponent',
  extends: imageHelper,
  props: {
    // props from imageHelper.js

    /**
     * Activate photoswipe lightbox on image
     */
    photoSwipe: {
      type: Boolean,
      default: false
    },
    coverDesktop: {
      type: Number,
      default: 100
    },
    coverMobile: {
      type: Number,
      default: 100
    },
    lazy: {
      type: Boolean,
      default: true
    },
    alt: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: true,
      loaded: false,
      observer: {}
    };
  },
  methods: {
    // methods inserted from imageHelper.js
  },
  watch: {
    imageObject: function (newvalue, oldvalue) {
      this.loaded = false;
      this.$refs.image.setAttribute('data-loaded', 'false');
      this.observer.observe();
    }
  },
  computed: {
    // computed inserted from imageHelper.js

    updateSrcFocal () {
      if (
        typeof this.imageObject === 'undefined' ||
        this.imageObject === null
      ) {
        return '';
      }
      return this.makeUrl(
        0,
        0,
        this.backgroundColor,
        '',
        'q_auto',
        'jpg',
        true
      );
    },
    updateSrcsetFocal () {
      if (
        typeof this.imageObject === 'undefined' ||
        this.imageObject === null
      ) {
        return '';
      }

      let str = '';
      for (let i = 0; i < this.srcScaleSteps.length; i++) {
        const url = this.makeUrl(
          0,
          0,
          this.backgroundColor,
          '',
          'q_auto',
          'jpg',
          true,
          this.srcScaleSteps[i] / 320
        );

        str += `${url} ${this.srcScaleSteps[i]}w`;
        if (i < this.srcScaleSteps.length - 1) {
          str += ',';
        }
      }

      return str;
    },
    sizes () {
      return `(min-width: 768px) ${this.coverDesktop}vw, ${this.coverMobile}vw`;
    },
    aspectRatio () {
      // Calculate the aspect ratio of the image
      // if the width and the height are given.
      if (!this.width || !this.height) return null;
      const xPadding = 0;
      const yPadding = 0;
      return ((this.height - yPadding) / (this.width + xPadding)) * 100;
    },
    style () {
      // The background color is used as a
      // placeholder while loading the image.
      // You can use the dominant color of the
      // image to improve perceived performance.
      // See: https://manu.ninja/dominant-colors-for-lazy-loading-images/
      const style = { backgroundColor: this.backgroundColor };

      // if (this.width) style.width = `${this.width}px`;

      // If the image is still loading and an
      // aspect ratio could be calculated, we
      // apply the calculated aspect ratio by
      // using padding top.
      const applyAspectRatio = this.loading && this.aspectRatio;
      if (applyAspectRatio) {
        // Prevent flash of unstyled image
        // after the image is loaded.
        style.height = 0;
        // Scale the image container according
        // to the aspect ratio.
        style.paddingTop = `${this.aspectRatio}%`;
      }
      return style;
    },
    imageRef () {
      return this.$refs.image;
    }
  },
  mounted () {
    if (this.photoSwipe) {
      this.enablePhotoSwipe();
    }
    // As soon as the <img> element triggers
    // the `load` event, the loading state is
    // set to `false`, which removes the apsect
    // ratio we've applied earlier.
    const setLoadingState = () => {
      this.loading = false;
      this.loaded = true;
    };
    this.imageRef.addEventListener('load', setLoadingState);
    // We remove the event listener as soon as
    // the component is destroyed to prevent
    // potential memory leaks.
    this.$once('hook:destroyed', () => {
      this.imageRef.removeEventListener('load', setLoadingState);
    });

    // We initialize Lozad.js on the root
    // element of our component.
    this.observer = lozad(this.imageRef);
    this.observer.observe();
  }
};
</script>

<style lang="scss">
.AppImage {
  padding-top: 0;
  vertical-align: middle;
  opacity: 0;
  visibility: hidden;
  transition: opacity 700ms ease 100ms;
}

.AppImage.-loaded {
  padding-top: 0;
  opacity: 1;
  visibility: visible;
}
</style>
